import { Component, ViewChild } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { FlexLayoutModule } from '@angular/flex-layout';
import { NzSelectModule, NzSelectOptionInterface } from 'ng-zorro-antd/select';
import { CommonModule } from '@angular/common';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { Subject, takeUntil } from 'rxjs';
import { IProblemDropdown } from '../../../../../../../interfaces/Problems/IProblemDropdown';
import { IAddProblem } from '../../../../../../../interfaces/Problems/IAddProblem';
import { ResolvedProblemsService } from '../../../../../../../services/resolvedProblems.service';
import { NzNoAnimationModule } from 'ng-zorro-antd/core/no-animation';
import { SwalToastService } from 'src/app/services/swal.service';
import {
  dateRangeValidator,
  dynamicDateRangeValidator,
} from 'src/date/date.configuration';

@Component({
  selector: 'app-resolved-problems-add-edit',
  standalone: true,
  templateUrl: './resolved-problems-add-edit.component.html',
  styleUrls: ['./resolved-problems-add-edit.component.css'],
  imports: [
    NzModalModule,
    TranslateModule,
    FormsModule,
    FlexLayoutModule,
    ReactiveFormsModule,
    NzSelectModule,
    CommonModule,
    NzButtonModule,
    NzTableModule,
    NzLayoutModule,
    NzIconModule,
    NzNoAnimationModule,
  ],
})
export class ResolvedProblemsAddEditComponent {
  private destroy$ = new Subject<void>();

  problemsDropdown: IProblemDropdown | undefined;
  idDropdown: NzSelectOptionInterface[] = [];
  rdDropdown: NzSelectOptionInterface[] = [];

  isVisible = false;
  isLoading = false;
  isIdLoading = false;
  isRdLoading = false;

  isEdit = false;
  editProblemsId?: number;

  problemsSubmitted = false;

  @ViewChild('modalRef', { static: true }) modalRef!: NzModalRef; // Reference to the modal

  problemsForm: FormGroup = new FormGroup({});

  constructor(
    private readonly resolvedProblemsService: ResolvedProblemsService,
    private readonly formBuilder: FormBuilder,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService
  ) {}

  private idValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const tvalue = control?.parent?.get('typeValue')?.value;
      const ivalue = control?.parent?.get('illnessDisorderValue')?.value;

      if (tvalue == 0) {
        if (!ivalue || ivalue == '' || ivalue == null) {
          this.problemsForm.get('illnessDisorderValue')?.setErrors({
            illnessDisorderValue: true,
          });

          return { illnessDisorderValue: true };
        }
      }

      this.problemsForm.get('illnessDisorderValue')?.setErrors(null);
      this.problemsForm.get('rareDiseaseValue')?.setErrors(null);

      return null;
    };
  }

  private rdValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const tvalue = control?.parent?.get('typeValue')?.value;
      const rvalue = control?.parent?.get('rareDiseaseValue')?.value;

      if (tvalue == 1) {
        if (!rvalue || rvalue == '' || rvalue == null) {
          this.problemsForm.get('rareDiseaseValue')?.setErrors({
            rareDiseaseValue: true,
          });

          return { rareDiseaseValue: true };
        }
      }

      this.problemsForm.get('illnessDisorderValue')?.setErrors(null);
      this.problemsForm.get('rareDiseaseValue')?.setErrors(null);

      return null;
    };
  }

  ngOnInit() {
    this.isLoading = true;
    this.isIdLoading = true;
    this.isRdLoading = true;

    this.isEdit = false;

    this.resolvedProblemsService.getProblemDropdown().subscribe({
      next: (pro) => {
        this.problemsDropdown = pro?.data;
        this.isLoading = false;
      },
      error: (err) => {},
    });

    this.resolvedProblemsService.getIllnessDisorderDropdown().subscribe({
      next: (pro) => {
        this.idDropdown = pro?.data?.illnessandDisorder.map((d) => {
          return {
            label: d.name,
            value: d.id,
          };
        });

        this.isIdLoading = false;
      },
      error: (err) => {},
    });

    this.resolvedProblemsService.getRareDiseaseDropdown().subscribe({
      next: (pro) => {
        this.rdDropdown = pro?.data?.rareDisease.map((d) => {
          return {
            label: d.name,
            value: d.id,
          };
        });

        this.isRdLoading = false;
      },
      error: (err) => {},
    });

    this.problemsForm = this.formBuilder.group({
      problemValue: [null, Validators.required],
      typeValue: [null, Validators.required],
      statusCodeValue: [null, Validators.required],
      diagnosisAssertionValue: [null, Validators.required],
      onSetDateValue: [
        null,
        [
          Validators.required,
          dateRangeValidator(
            undefined,
            this?.problemsForm?.value?.endDateValue || this.getCurrentDate()
          ),
        ],
      ],
      endDateValue: [
        null,
        [
          Validators.required,
          dynamicDateRangeValidator(
            () => this.problemsForm.get('onSetDateValue')?.value,
            () => new Date()
          ),
        ],
      ],
      descriptionValue: [null, Validators.required],
      resolutionCircumstancesTextValue: [null, Validators.required],
      rareDiseaseValue: [null, this.rdValidator()],
      illnessDisorderValue: [null, this.idValidator()],
    });

    this.resolvedProblemsService.resolvedProblemEditSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        // GET DATA FIELDS AND ASSIGN DEFAULT VALUES TO FORM
        if (data) {
          this.isEdit = true;
          this.editProblemsId = data?.id;

          this.problemsForm.patchValue({
            problemValue: data?.problemId,
            typeValue: data?.illnessAndDisorderId ? 0 : 1,
            statusCodeValue: data?.statusCodeId,
            diagnosisAssertionValue: data?.diagnosisAssertionId,
            onSetDateValue: data?.onSetDate,
            endDateValue: data?.endDate,
            resolutionCircumstancesTextValue: data?.resolutionCircumstancesText,
            descriptionValue: data?.description,
            rareDiseaseValue: data?.rareDiseaseId,
            illnessDisorderValue: data?.illnessAndDisorderId,
          });
        } else {
          this.isEdit = false;
          this.editProblemsId = undefined;

          this?.problemsForm.reset();
        }
      });

    this.resolvedProblemsService.resolvedProblemModalSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((showModal) => {
        this.isVisible = showModal;
      });
  }

  ngOnDestroy() {
    // Unsubscribe from observables
    this.destroy$.next();
    this.destroy$.complete();

    this?.modalRef.destroy();
  }

  getCurrentDate() {
    return new Date().toISOString().split('T')[0];
  }

  handleCancel() {
    this.problemsForm.reset();

    this.resolvedProblemsService.resolvedProblemModalSub.next(false);

    this.problemsSubmitted = false;

    this.isLoading = false;

    this.modalRef.destroy();
  }

  submitForm() {
    this.isLoading = true;
    this.problemsSubmitted = true;

    if (this.problemsForm.valid) {
      const problemObj: IAddProblem = {};

      problemObj.id = this.editProblemsId;
      problemObj.problemId = this.problemsForm.value?.problemValue;
      problemObj.statusCodeId = this.problemsForm.value?.statusCodeValue;
      problemObj.diagnosisAssertionId =
        this.problemsForm.value?.diagnosisAssertionValue;
      problemObj.onSetDate = this.problemsForm.value?.onSetDateValue;
      problemObj.endDate = this.problemsForm.value?.endDateValue;
      problemObj.resolutionCircumstancesText =
        this.problemsForm.value?.resolutionCircumstancesTextValue;
      problemObj.description = this.problemsForm.value?.descriptionValue;

      if (this.problemsForm.value?.typeValue == 0) {
        problemObj.illnessAndDisorderId =
          this.problemsForm.value?.illnessDisorderValue;
      }

      if (this.problemsForm.value?.typeValue == 1) {
        problemObj.rareDiseaseId = this.problemsForm.value?.rareDiseaseValue;
      }

      if (this.isEdit) {
        this.resolvedProblemsService.editProblem(problemObj).subscribe({
          next: () => {
            this.swalToastService.toastSuccess(
              'Resolved Problem edited successfully'
            );
            this.resolvedProblemsService.getData();

            this.handleCancel();
          },
          error: (err) => {
            this.swalToastService.toastError('Unable to edit resolved problem');
            this.handleCancel();
          },
        });
      } else {
        this.resolvedProblemsService.addProblem(problemObj).subscribe({
          next: () => {
            this.swalToastService.toastSuccess(
              'Resolved Problem added successfully'
            );
            this.resolvedProblemsService.getData();

            this.handleCancel();
          },
          error: (err) => {
            this.swalToastService.toastError('Unable to add resolved problem');
            this.handleCancel();
          },
        });
      }
    } else {
      this.isLoading = false;
    }
  }
}
